import { useEffect, useState } from 'react';
import { getFavorites, getRatings, deleteFavorite, addOrUpdateRating } from '../services/api';
import BookCard from '../components/BookCard';
import './MyBooksPage.css';

function MyBooksPage() {
  const [username, setUsername] = useState(null);
  const [userId, setUserId] = useState(null);
  const [favoriteBooks, setFavoriteBooks] = useState([]);
  const [ratedBooks, setRatedBooks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('favorites');

  useEffect(() => {
    const storedUsername = localStorage.getItem('username');
    const storedUserId = localStorage.getItem('userId');
    if (storedUserId && storedUsername) {
      setUsername(storedUsername);
      setUserId(storedUserId);
      fetchBooks(storedUserId);
    } else {
      console.log('User not logged in');
    }
  }, []);

  const handleRating = async (book, ratingValue) => {
    const isLoggedIn = !!localStorage.getItem('username');
    if (!isLoggedIn) {
      alert('You need to be logged in to rate books.');
      return;
    }
    try {
      await addOrUpdateRating(userId, book, ratingValue);
      setRatedBooks((prevBooks) =>
        prevBooks.map((b) =>
          b.id === book.id ? { ...b, userRating: ratingValue } : b
        )
      );
      
    } catch (err) {
      console.error('Failed to rate book:', err);
    }
  };
  
  const fetchBooks = async (id) => {
    setLoading(true);
    try {
      const [favoritesData, ratingsData] = await Promise.all([
        getFavorites(id),
        getRatings(id),
      ]);
      setFavoriteBooks(favoritesData || []);
      setRatedBooks(ratingsData || []);
    } catch (err) {
      console.error('Error fetching user books:', err);
    } finally {
      setLoading(false);
    }
  };
  
  const handleTabSwitch = (tab) => {
    setActiveTab(tab);
  };

  const handleDeleteFavorite = async (bookId) => {
    if (!userId || !bookId) return
    const confirmDelete = window.confirm("Are you sure you want to remove this book from your favorites?")
    if (!confirmDelete) return
    try {
      await deleteFavorite(userId, bookId)
  
      setFavoriteBooks((prev) => prev.filter((book) => book.id !== bookId))
  
      setRatedBooks((prev) => prev.map((book) => (book.id === bookId ? { ...book, isFavorite: false } : book)))
    } catch (err) {
      console.error("Error deleting favorite:", err)
     
    }
  }
  
  // Custom Skeleton Loader Component
  const BookCardSkeleton = () => {
    return (
      <div className="book-card-skeleton">
        <div className="book-cover-skeleton"></div>
        <div className="book-info-skeleton">
          <div className="book-title-skeleton"></div>
          <div className="book-author-skeleton"></div>
          <div className="book-rating-skeleton">
            <div className="star-skeleton"></div>
            <div className="star-skeleton"></div>
            <div className="star-skeleton"></div>
            <div className="star-skeleton"></div>
            <div className="star-skeleton"></div>
          </div>
          <div className="book-actions-skeleton">
            <div className="action-button-skeleton"></div>
            <div className="action-button-skeleton"></div>
          </div>
        </div>
      </div>
    );
  };

  // Function to render multiple skeleton cards
  const renderSkeletonCards = (count) => {
    return Array(count).fill(0).map((_, index) => (
      <BookCardSkeleton key={`skeleton-${index}`} />
    ));
  };

  if (!userId || !username) {
    return (
      <div className="my-books-page">
        <div className="my-books-container">
          <div className="my-books-header">
            <h2 className="page-title">My Bookshelf</h2>
          </div>
          <div className="not-logged-in">
            <div className="icon-placeholder">📚</div>
            <h3>Please log in to see your books</h3>
            <p>Sign in to access your favorites and rated books</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="my-books-page">
      <div className="my-books-container">
        <div className="my-books-header">
          <h2 className="page-title">{username}'s Bookshelf</h2>
        </div>
        <div className="tabs-container">
          <div className="tabs">
            <button
              onClick={() => handleTabSwitch('favorites')}
              className={`tab-button ${activeTab === 'favorites' ? 'active' : ''}`}
            >
              <span className="tab-icon">❤️</span> Favorites
            </button>
            <button
              onClick={() => handleTabSwitch('rated')}
              className={`tab-button ${activeTab === 'rated' ? 'active' : ''}`}
            >
              <span className="tab-icon">⭐</span> Rated Books
            </button>
          </div>
        </div>
        {loading ? (
          <div className="books-grid skeleton-grid">
            {renderSkeletonCards(8)}
          </div>
        ) : (
          <div className="books-list">
            {activeTab === 'favorites' && favoriteBooks.length > 0 && (
              <div className="books-grid">
                {favoriteBooks.map((book) => (
                  <BookCard 
                    key={book.id} 
                    book={book} 
                    showDeleteButton={true}
                    onDelete={() => handleDeleteFavorite(book.id)}
                    showDetailsLink={true}
                    linkTo={`/favorite/${book.id}`}
                    linkState={{ bookId: book.id }}
                    showRatingButton={false}
                    showFavoriteButton={false}
                    isFavorite={true}
                  />
                ))}
              </div>
            )}
            {activeTab === 'rated' && ratedBooks.length > 0 && (
              <div className="books-grid">
                {ratedBooks.map((book) => (
                <BookCard 
                  key={book.id} 
                  book={book} 
                  showRating={true}
                  userRating={book.userRating}
                  onRate={(value) => handleRating(book, value)}
                  showDetailsLink={true}
                  linkTo={`/favorite/${book.id}`}
                  linkState={{ bookId: book.id, book }}
                  showRatingButton={true}
                  showFavoriteButton={false}
                  isFavorite={favoriteBooks.some(favBook => favBook.id === book.id)}
                />
              ))}
              </div>
            )}
            {(activeTab === 'favorites' && favoriteBooks.length === 0) ||
              (activeTab === 'rated' && ratedBooks.length === 0) ? (
              <div className="empty-state">
                <div className="empty-icon">
                  {activeTab === 'favorites' ? '📚' : '⭐'}
                </div>
                <h3>No {activeTab === 'favorites' ? 'favorite' : 'rated'} books yet</h3>
                <p>
                  {activeTab === 'favorites'
                    ? 'Books you save as favorites will appear here'
                    : 'Books you rate will appear here'}
                </p>
              </div>
            ) : null}
          </div>
        )}
      </div>
    </div>
  );
}

export default MyBooksPage;